import React, { useState, useEffect } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../store';
import { teamService } from '../../services';
import UserControls from './UserControls';

// ============================================
// Icons
// ============================================

const LogoMark = () => (
  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13 10V3L4 14h7v7l9-11h-7z"
    />
  </svg>
);

// Three small blocks + a plus in the fourth slot — implies modular,
// composable building blocks (heroicons "squares-plus").
const WidgetsIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
      d="M13.5 16.875h3.375m0 0h3.375m-3.375 0V13.5m0 3.375v3.375M6 10.5h2.25a2.25 2.25 0 002.25-2.25V6a2.25 2.25 0 00-2.25-2.25H6A2.25 2.25 0 003.75 6v2.25A2.25 2.25 0 006 10.5zm0 9.75h2.25A2.25 2.25 0 0010.5 18v-2.25a2.25 2.25 0 00-2.25-2.25H6a2.25 2.25 0 00-2.25 2.25V18A2.25 2.25 0 006 20.25zm9.75-9.75H18a2.25 2.25 0 002.25-2.25V6A2.25 2.25 0 0018 3.75h-2.25A2.25 2.25 0 0013.5 6v2.25a2.25 2.25 0 002.25 2.25z" />
  </svg>
);

const DashboardsIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
      d="M4 5a1 1 0 011-1h5a1 1 0 011 1v5a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM13 5a1 1 0 011-1h5a1 1 0 011 1v5a1 1 0 01-1 1h-5a1 1 0 01-1-1V5zM4 14a1 1 0 011-1h5a1 1 0 011 1v5a1 1 0 01-1 1H5a1 1 0 01-1-1v-5zM13 14a1 1 0 011-1h5a1 1 0 011 1v5a1 1 0 01-1 1h-5a1 1 0 01-1-1v-5z" />
  </svg>
);

const WorkflowsIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
  </svg>
);

const TasksIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
  </svg>
);

const TeamIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
      d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-3.13a4 4 0 11-8 0 4 4 0 018 0zm6 0a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const CoverageIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
      d="M3 6l6-3 6 3 6-3v15l-6 3-6-3-6 3V6zM9 3v15M15 6v15" />
  </svg>
);

// Concentric signal arcs — "listening for events / firing on signals"
// (heroicons "signal")
const TriggersIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
      d="M9.348 14.652a3.75 3.75 0 010-5.304m5.304 0a3.75 3.75 0 010 5.304m-7.425 2.121a6.75 6.75 0 010-9.546m9.546 0a6.75 6.75 0 010 9.546M5.106 18.894c-3.808-3.807-3.808-9.98 0-13.788m13.788 0c3.808 3.807 3.808 9.98 0 13.788M12 12h.008v.008H12V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
  </svg>
);

// ============================================
// Sidebar nav item
// ============================================

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  prefixMatch?: string;
}

interface SidebarNavButtonProps {
  item: NavItem;
  active: boolean;
}

const SidebarNavButton: React.FC<SidebarNavButtonProps> = ({ item, active }) => (
  <Link
    to={item.path}
    title={item.label}
    aria-label={item.label}
    className={`
      relative flex items-center justify-center w-10 h-10 rounded-md transition-colors
      ${active
        ? 'bg-primary-50 text-primary-600'
        : 'text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100'}
    `}
  >
    {item.icon}
  </Link>
);

// ============================================
// Full-screen builder detection
// ============================================

/**
 * Routes that take the entire main area (no PageTopBar). On these pages
 * the sidebar shows the user controls cluster at the bottom, since the
 * top bar isn't there to host them.
 */
const FULL_SCREEN_BUILDER_PATHS = [
  '/dashboard-builder',
  '/workflow-builder',
  '/task/',
];

const isFullScreenBuilderRoute = (pathname: string): boolean =>
  FULL_SCREEN_BUILDER_PATHS.some(p => pathname.startsWith(p));

// ============================================
// Sidebar
// ============================================

const Sidebar: React.FC = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isEmbed = searchParams.get('embed') === 'true';
  const { user, isAuthenticated } = useAuthStore();
  const [hasTeam, setHasTeam] = useState<boolean | null>(null);

  const isActive = (path: string) => location.pathname === path;
  const isActivePrefix = (prefix: string) => location.pathname.startsWith(prefix);
  const isItemActive = (item: NavItem) =>
    isActive(item.path) || (item.prefixMatch ? isActivePrefix(item.prefixMatch) : false);

  const isOwner = user?.role === 'owner' || user?.role === 'admin';
  const showUserControlsHere = isFullScreenBuilderRoute(location.pathname);

  // Check team status for workers
  useEffect(() => {
    const checkTeamStatus = async () => {
      if (!isAuthenticated || isOwner) return;

      try {
        const status = await teamService.getTeamStatus();
        setHasTeam(status.hasTeam ?? false);
      } catch (err) {
        console.error('Failed to check team status:', err);
        setHasTeam(false);
      }
    };

    checkTeamStatus();
  }, [isAuthenticated, isOwner]);

  if (!isAuthenticated || isEmbed) return null;

  // Owner navigation (Dashboard is reached via the logo)
  // Owner navigation, grouped by purpose. Groups render with a divider
  // between them in the rail.
  //   1. Build       — libraries / builders for widgets, dashboards, workflows
  //   2. Automation  — triggers that fire workflows on real-world events
  //   3. Org         — people and what they cover
  const ownerNavGroups: NavItem[][] = [
    [
      { path: '/widgets', label: 'Widgets', icon: <WidgetsIcon /> },
      { path: '/dashboards', label: 'Dashboards', icon: <DashboardsIcon />, prefixMatch: '/dashboard-builder' },
      { path: '/workflows', label: 'Workflows', icon: <WorkflowsIcon />, prefixMatch: '/workflow-builder' },
    ],
    [
      { path: '/triggers', label: 'Triggers', icon: <TriggersIcon /> },
    ],
    [
      { path: '/team', label: 'Team', icon: <TeamIcon /> },
      { path: '/coverage', label: 'Coverage', icon: <CoverageIcon /> },
    ],
  ];

  // Worker navigation — single small group, no divider needed.
  const workerNavGroups: NavItem[][] = [
    [
      { path: '/dashboard', label: 'My Tasks', icon: <TasksIcon />, prefixMatch: '/task/' },
      ...(hasTeam ? [{ path: '/coverage', label: 'Coverage', icon: <CoverageIcon /> }] : []),
      ...(hasTeam === false
        ? [{ path: '/join-team', label: 'Join Team', icon: <TeamIcon /> }]
        : []),
    ],
  ];

  const navGroups = isOwner ? ownerNavGroups : workerNavGroups;

  return (
    <aside className="flex flex-col w-14 shrink-0 bg-surface z-40">
      {/* Logo */}
      <Link
        to="/dashboard"
        className="flex items-center justify-center h-14 shrink-0 border-b border-neutral-200"
        aria-label="Orchestrate home"
      >
        <div className="w-8 h-8 bg-primary-600 rounded-md flex items-center justify-center">
          <LogoMark />
        </div>
      </Link>

      {/* Primary nav */}
      <nav className="flex flex-col items-center py-3 flex-1 overflow-y-auto no-scrollbar">
        {navGroups.map((group, groupIdx) => (
          <React.Fragment key={groupIdx}>
            {groupIdx > 0 && (
              <div className="my-2 h-px w-6 bg-neutral-200" aria-hidden="true" />
            )}
            <div className="flex flex-col items-center gap-1">
              {group.map(item => (
                <SidebarNavButton key={item.path} item={item} active={isItemActive(item)} />
              ))}
            </div>
          </React.Fragment>
        ))}
      </nav>

      {/* Bottom controls — only shown on full-screen builder pages where
          there is no PageTopBar to host the user cluster. */}
      {showUserControlsHere && (
        <div className="py-3 border-t border-neutral-200">
          <UserControls direction="vertical" />
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
